/* eslint-disable @reatom/unit-naming-rule */
import { atom, type Atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { AsyncStateN, Iterable, pipe } from '@blade/std-lib'

import type { WalletModel } from './reatom-wallet'
import type { CurrencyAddress } from '@root/entities/currency'
import type { Error } from '@root/shared'

export const reatomUserWalletsBalances = ({
  walletsStateAtom,
}: {
  walletsStateAtom: Atom<
    AsyncStateN.AsyncStateN<Error.UnknownError, Array<WalletModel>>
  >
}) => {
  const totalBalanceNativeStateAtom = atom((ctx) => {
    return ctx.spy(walletsStateAtom).pipe(
      AsyncStateN.map((wallets) =>
        pipe(
          wallets,
          Iterable.reduce(BigNumber(0), (acc, wallet) => {
            const totalBalanceNative = ctx.spy(wallet.totalBalanceNative)
            return acc.plus(totalBalanceNative)
          }),
        ),
      ),
    )
  })

  const totalBalanceNativeAtom = atom((ctx) => {
    return ctx.spy(totalBalanceNativeStateAtom).value ?? BigNumber(0)
  })

  const totalBalanceUsdStateAtom = atom((ctx) => {
    return ctx.spy(walletsStateAtom).pipe(
      AsyncStateN.map((wallets) =>
        pipe(
          wallets,
          Iterable.reduce(BigNumber(0), (acc, wallet) => {
            const totalBalanceUsd = ctx.spy(wallet.totalBalanceUsd)
            return acc.plus(totalBalanceUsd)
          }),
        ),
      ),
    )
  })

  const totalBalanceUsdAtom = atom((ctx) => {
    return ctx.spy(totalBalanceUsdStateAtom).value ?? BigNumber(0)
  })

  const nativeAssetBalanceStateAtom = atom((ctx) => {
    return ctx.spy(walletsStateAtom).pipe(
      AsyncStateN.map((wallets) =>
        pipe(
          wallets,
          Iterable.reduce(BigNumber(0), (acc, wallet) => {
            const balance = ctx.spy(wallet.nativeAssetBalance)
            return acc.plus(balance)
          }),
        ),
      ),
    )
  })

  const nativeAssetBalanceAtom = atom((ctx) => {
    return ctx.spy(nativeAssetBalanceStateAtom).value ?? BigNumber(0)
  })

  const assetBalanceState = (currencyAddress: CurrencyAddress) => {
    return atom((ctx) => {
      return ctx.spy(walletsStateAtom).pipe(
        AsyncStateN.map((wallets) => {
          return wallets.reduce((acc, wallet) => {
            const assetsByCurrency = ctx.spy(wallet.assetsByCurrency)
            const asset = assetsByCurrency[currencyAddress]
            if (!asset) {
              return acc
            }
            const assetBalance = ctx.spy(asset.balance)
            return acc.plus(assetBalance)
          }, BigNumber(0))
        }),
      )
    })
  }

  const assetBalance = (currencyAddress: CurrencyAddress) => {
    const stateAtom = assetBalanceState(currencyAddress)
    return atom((ctx) => ctx.spy(stateAtom).value ?? BigNumber(0))
  }

  const assetBalanceUsdState = (currencyAddress: CurrencyAddress) => {
    return atom((ctx) => {
      return ctx.spy(walletsStateAtom).pipe(
        AsyncStateN.map((wallets) => {
          return wallets.reduce((acc, wallet) => {
            const assetsByCurrency = ctx.spy(wallet.assetsByCurrency)
            const asset = assetsByCurrency[currencyAddress]
            if (!asset) {
              return acc
            }
            const assetBalanceUsd = ctx.spy(asset.balanceUsd)
            return acc.plus(assetBalanceUsd)
          }, BigNumber(0))
        }),
      )
    })
  }
  const assetBalanceUsd = (currencyAddress: CurrencyAddress) => {
    const stateAtom = assetBalanceUsdState(currencyAddress)
    return atom((ctx) => ctx.spy(stateAtom).value ?? BigNumber(0))
  }

  return {
    totalBalanceNativeState: totalBalanceNativeStateAtom,
    totalBalanceNative: totalBalanceNativeAtom,
    totalBalanceUsdState: totalBalanceUsdStateAtom,
    totalBalanceUsd: totalBalanceUsdAtom,
    nativeAssetBalanceState: nativeAssetBalanceStateAtom,
    nativeAssetBalance: nativeAssetBalanceAtom,
    assetBalance,
    assetBalanceUsd,
  }
}
